import * as brandsConfig from '../providers/BrandConfigContext/config/coreBrandConfs'
import { stripHtmlTags } from '../utils/htmlUtils'

// Constants
const SCHEMA_ORG_CONTEXT = 'https://schema.org'
const BEST_RATING = 5
const WORST_RATING = 1
const CURRENCY = 'GBP'

// Get current brand configuration (cached)
const getCurrentBrandConfig = () => brandsConfig[(process.env.BRAND || 'ARGOS').toLowerCase()]

/**
 * Generates Product structured data for the Product Detail Page
 * @param {Object} productStore - The product store data from Redux
 * @param {Object} bazaarVoiceReviewStore - Optional BazaarVoice reviews data
 * @returns {Object|null} Product structured data object
 */
export const generateProductStructuredData = (productStore, bazaarVoiceReviewStore = null) => {
  const productData = productStore?.data
  if (!productData?.attributes) {
    return null
  }

  const attributes = productData.attributes
  const prices = productData.prices?.attributes
  const brandConfig = getCurrentBrandConfig()

  // Construct product URL from SKU and brand base URL
  const productUrl = `${brandConfig.canonicalBase}/product/${attributes.partNumber}`

  const productStructuredData = {
    '@type': 'Product',
    name: productData.productName,
    description: stripHtmlTags(attributes.description),
    sku: attributes.partNumber,
    url: productUrl,
    mainEntityOfPage: productUrl,
    itemCondition: 'https://schema.org/NewCondition',
    image: `https://media.4rgos.it/s/Argos/${attributes.partNumber}_R_SET`
  }

  // Add brand if it exists
  if (attributes.brand) {
    productStructuredData.brand = {
      '@type': 'Brand',
      name: attributes.brand
    }
  }

  // Add aggregate rating if available
  if (bazaarVoiceReviewStore?.data?.stats?.attributes) {
    const stats = bazaarVoiceReviewStore.data.stats.attributes

    if (stats.reviewCount > 0 && stats.overallRating) {
      productStructuredData.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: stats.overallRating.toFixed(2),
        reviewCount: stats.reviewCount,
        bestRating: BEST_RATING,
        worstRating: WORST_RATING
      }
    }
  }

  // Add price and availability if available
  if (prices?.now) {
    productStructuredData.offers = {
      '@type': 'Offer',
      price: prices.now.toFixed(2),
      priceCurrency: CURRENCY,
      // Availability will be injected dynamically after frontend availability fetch
      url: productUrl,
      seller: {
        '@type': 'Organization',
        name: brandConfig.brandDisplayName
      }
    }
  }

  return productStructuredData
}

/**
 * Generates Breadcrumb structured data for navigation
 * @param {Array} breadcrumb - The breadcrumb data array
 * @param {string} currentProductName - The current product name
 * @returns {Object|null} Breadcrumb structured data object
 */
export const generateBreadcrumbStructuredData = (breadcrumb, currentProductName) => {
  // Early return if no breadcrumb data
  if ((!breadcrumb || breadcrumb.length === 0) && !currentProductName) {
    return null
  }

  const brandConfig = getCurrentBrandConfig()
  const itemListElement = []

  // Add breadcrumb items
  if (breadcrumb?.length > 0) {
    breadcrumb.forEach((item, index) => {
      itemListElement.push({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url ? `${brandConfig.canonicalBase}${item.url}` : undefined
      })
    })
  }

  // Add current product as final item
  if (currentProductName) {
    itemListElement.push({
      '@type': 'ListItem',
      position: itemListElement.length + 1,
      name: currentProductName
    })
  }

  return {
    '@type': 'BreadcrumbList',
    itemListElement
  }
}

/**
 * Generates complete structured data for the PDP page
 * @param {Object} productStore - The product store data from Redux
 * @param {string} canonicalUrl - The canonical URL for the product
 * @param {Object} bazaarVoiceReviewStore - Optional BazaarVoice reviews data
 * @param {boolean} isQuestionRoute - Whether this is a question page (no JSON-LD for noindex pages)
 * @param {boolean} isPromotionPage - Whether this is a promotion page (no JSON-LD for noindex pages)
 * @param {boolean} isReviewRoute - Whether this is a review form page (no JSON-LD for form pages)
 * @returns {string|null} Complete structured data as JSON string ready for script tag, or null if no data
 */
export const generateStructuredData = (
  productStore,
  canonicalUrl,
  bazaarVoiceReviewStore = null,
  isQuestionRoute = false,
  isPromotionPage = false,
  isReviewRoute = false
) => {
  // Exclude question pages, promotion pages, and review form pages
  if (isQuestionRoute || isPromotionPage || isReviewRoute) {
    return null
  }

  try {
    const structuredDataItems = []

    // Generate product structured data
    const productStructuredData = generateProductStructuredData(productStore, bazaarVoiceReviewStore)
    if (productStructuredData) {
      structuredDataItems.push(productStructuredData)
    }

    // Generate breadcrumb structured data
    const breadcrumbStructuredData = generateBreadcrumbStructuredData(
      productStore?.data?.breadcrumb || [],
      productStore?.data?.productName
    )
    if (breadcrumbStructuredData) {
      structuredDataItems.push(breadcrumbStructuredData)
    }

    if (structuredDataItems.length === 0) {
      return null
    }

    const structuredDataObject = {
      '@context': SCHEMA_ORG_CONTEXT,
      '@graph': structuredDataItems
    }

    // Pretty print in development, minified in production
    const isDevelopment = process.env.MESOS_ENV === 'dev'
    return isDevelopment ? JSON.stringify(structuredDataObject, null, 2) : JSON.stringify(structuredDataObject)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error generating structured data:', error)
    return null
  }
}

/**
 * Updates the structured data in the DOM with availability information
 * @param {string} partNumber - The main product part number
 * @param {boolean} isAvailable - Whether the product is available based on frontend data
 */
export const updateStructuredDataAvailability = (partNumber, isAvailable) => {
  if (typeof window === 'undefined') return // Server-side rendering

  try {
    // Find the structured data script tag
    const structuredDataScript = document.querySelector('script[type="application/ld+json"]')
    if (!structuredDataScript) return

    const structuredDataObject = JSON.parse(structuredDataScript.textContent)

    // Navigate to the product in the @graph array
    if (structuredDataObject['@graph']) {
      const productData = structuredDataObject['@graph'].find(item => item['@type'] === 'Product')

      if (productData?.offers) {
        // Update availability based on frontend data
        productData.offers.availability = isAvailable ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock'

        // Update the script tag content
        const isDevelopment = process.env.MESOS_ENV === 'dev'
        structuredDataScript.textContent = isDevelopment
          ? JSON.stringify(structuredDataObject, null, 2)
          : JSON.stringify(structuredDataObject)
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error updating structured data availability:', error)
  }
}
